import {useEffect, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";

import {ContractAPI} from "@/api/contract";
import {PlayerAPI} from "@/api/player";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";

import {
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
} from "@/utils/constants";
import {checkRequiredFields, playerToOption} from "@/utils/functions";

import "./index.scss";

const contractTypeOptions = [
	{value: "professional", label: "Professionistico"},
	{value: "amateur", label: "Dilettantistico"},
	{value: "youth", label: "Settore giovanile"},
	{value: "loan", label: "Prestito"},
	{value: "amateur_professional", label: "Semiprofessionistico"},
];

const currencyOptions = [
	{value: "EUR", label: "EUR €"},
	{value: "USD", label: "USD $"},
	{value: "GBP", label: "GBP £"},
];

const bonusTypeOptions = [
	{value: "appearance", label: "Presenze"},
	{value: "goal", label: "Gol"},
	{value: "assist", label: "Assist"},
	{value: "championship", label: "Titolo/campionato"},
	{value: "promotion", label: "Promozione"},
	{value: "other", label: "Altro"},
];

const frequencyOptions = [
	{value: "per_occurrence", label: "Per occorrenza"},
	{value: "per_season", label: "Per stagione"},
	{value: "one_time", label: "Una tantum"},
];

const emptyBonus = () => ({
	_key: Date.now() + Math.random(),
	description: "",
	bonusType: null,
	amount: "",
	condition: "",
	frequency: null,
	maxOccurrencesPerSeason: "",
	notes: "",
});

const HandleContractPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const [searchParams] = useSearchParams();
	const playerIdFromQuery = searchParams.get("playerId");

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [playerName, setPlayerName] = useState("");
	const [playerOptions, setPlayerOptions] = useState([]);
	const [selectedPlayer, setSelectedPlayer] = useState(null);
	const [existingContracts, setExistingContracts] = useState([]);
	const [fields, setFields] = useState({
		playerId: playerIdFromQuery || "",
		contractType: null,
		signingDate: null,
		startDate: null,
		endDate: null,
		transferFee: "",
		currency: currencyOptions[0],
		fixedMonthlySalary: "",
		totalMonthlySalary: "",
		annualGrossSalary: "",
		signingBonus: "",
		releaseClause: "",
		isActive: true,
		status: null,
		notes: "",
	});
	const [bonuses, setBonuses] = useState([]);

	const loadPlayerName = async (pid) => {
		if (!pid) return;
		try {
			const p = await PlayerAPI.getById(pid);
			setPlayerName((p.lastName || "") + " " + (p.firstName || ""));
		} catch {
			// ignore
		}
	};

	const loadPlayers = async () => {
		try {
			const list = await PlayerAPI.list({prospect: false});
			const options = (list || [])
				.sort((a, b) => (a.lastName ?? "").localeCompare(b.lastName ?? ""))
				.map((p) => playerToOption(p));
			setPlayerOptions(options);
			if (playerIdFromQuery) {
				const match = options.find((o) => o.value === playerIdFromQuery);
				if (match) {
					setSelectedPlayer(match);
					setPlayerName(match.label);
					checkExistingContracts(playerIdFromQuery);
				}
			}
		} catch {
			// ignore
		}
	};

	const checkExistingContracts = async (playerId) => {
		if (!playerId) {
			setExistingContracts([]);
			return;
		}
		try {
			const list = await ContractAPI.listByPlayer(playerId);
			const active = (list || []).filter((c) => c.isActive);
			setExistingContracts(active);
		} catch {
			setExistingContracts([]);
		}
	};

	const getContract = async () => {
		setLoading(true);
		try {
			const response = await ContractAPI.getById(id);
			await loadPlayerName(response.playerId);
			setFields({
				...response,
				contractType:
					contractTypeOptions.find((o) => o.value === response.contractType) ||
					null,
				currency:
					currencyOptions.find((o) => o.value === response.currency) ||
					currencyOptions[0],
				isActive: response.isActive,
			});
			setBonuses(
				(response.bonuses || []).map((b) => ({
					...b,
					_key: b.id || Date.now() + Math.random(),
					bonusType:
						bonusTypeOptions.find((o) => o.value === b.bonusType) || null,
					frequency:
						frequencyOptions.find((o) => o.value === b.frequency) || null,
				})),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getContract();
		} else {
			loadPlayers();
		}
	}, [edit]);

	const getRequiredFields = () => [
		"playerId",
		"contractType",
		"signingDate",
		"startDate",
		"endDate",
		"totalMonthlySalary",
	];

	const saveContract = async () => {
		try {
			const formErrors = checkRequiredFields(getRequiredFields(), {
				...fields,
				contractType: fields.contractType,
				totalMonthlySalary:
					fields.totalMonthlySalary === "" ? null : fields.totalMonthlySalary,
			});
			setErrors(formErrors);
			if (formErrors.length) return;

			setLoading(true);

			const body = {
				...fields,
				contractType: fields.contractType?.value,
				currency: fields.currency?.value || "EUR",
				signingDate: fields.signingDate
					? moment(fields.signingDate).format("YYYY-MM-DD")
					: undefined,
				startDate: fields.startDate
					? moment(fields.startDate).format("YYYY-MM-DD")
					: undefined,
				endDate: fields.endDate
					? moment(fields.endDate).format("YYYY-MM-DD")
					: undefined,
				transferFee:
					fields.transferFee !== "" ? parseFloat(fields.transferFee) : null,
				fixedMonthlySalary:
					fields.fixedMonthlySalary !== ""
						? parseFloat(fields.fixedMonthlySalary)
						: null,
				totalMonthlySalary:
					fields.totalMonthlySalary !== ""
						? parseFloat(fields.totalMonthlySalary)
						: null,
				annualGrossSalary:
					fields.annualGrossSalary !== ""
						? parseFloat(fields.annualGrossSalary)
						: null,
				signingBonus:
					fields.signingBonus !== "" ? parseFloat(fields.signingBonus) : null,
				releaseClause:
					fields.releaseClause !== "" ? parseFloat(fields.releaseClause) : null,
				bonuses: bonuses.map((b) => ({
					...b,
					bonusType: b.bonusType?.value || null,
					frequency: b.frequency?.value || null,
					amount: parseFloat(b.amount) || 0,
					maxOccurrencesPerSeason:
						b.maxOccurrencesPerSeason !== ""
							? parseInt(b.maxOccurrencesPerSeason)
							: null,
				})),
			};

			if (edit) {
				body.id = id;
				await ContractAPI.update(id, body);
			} else {
				await ContractAPI.create(body);
			}

			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/players/" + fields.playerId);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const goBack = () => {
		if (fields.playerId) {
			navigate("/players/" + fields.playerId);
		} else {
			navigate("/manager/players-contracts");
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: saveContract,
			disabled:
				checkRequiredFields(getRequiredFields(), {
					...fields,
					contractType: fields.contractType,
					totalMonthlySalary:
						fields.totalMonthlySalary === "" ? null : fields.totalMonthlySalary,
				}).length > 0,
		},
	];

	const breadcrumbsElements = [
		{label: "Contratti", path: "/manager/players-contracts"},
	];
	if (playerName) {
		breadcrumbsElements.push(
			{label: "/"},
			{label: playerName, path: "/players/" + fields.playerId},
		);
	}

	const updateBonus = (index, key, value) => {
		setBonuses((prev) => {
			const updated = [...prev];
			updated[index] = {...updated[index], [key]: value};
			return updated;
		});
	};

	const removeBonus = (index) => {
		setBonuses((prev) => prev.filter((_, i) => i !== index));
	};

	const fieldChange = (key, value) => {
		setErrors([]);
		setFields((f) => ({...f, [key]: value}));
	};

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuovo contratto"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row g-2 no-margin">
					{/* Card dati principali */}
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									{!edit && (
										<div className="col-sm-12 col-md-6 col-lg-4">
											{playerIdFromQuery ? (
												<VLFTextField
													value={playerName}
													label="Giocatore"
													size="small"
													disabled
												/>
											) : (
												<>
													<VLFSelectNew
														options={playerOptions}
														sortKey="sortKey"
														value={selectedPlayer}
														onChange={(v) => {
															setSelectedPlayer(v);
															fieldChange("playerId", v?.value || "");
															setPlayerName(v ? v.label : "");
															checkExistingContracts(v?.value || null);
														}}
														label="Giocatore"
														required
														placeholder="Cerca giocatore..."
														helperText={
															errors.indexOf("playerId") !== -1
																? REQUIRED_FIELD
																: ""
														}
														error={errors.indexOf("playerId") !== -1}
													/>
													{existingContracts.length > 0 && (
														<div className="hc-existing-contract-warning">
															<span className="hc-existing-contract-warning__icon">
																⚠
															</span>
															<span>
																{existingContracts.length === 1
																	? "Questo giocatore ha già un contratto attivo"
																	: `Questo giocatore ha già ${existingContracts.length} contratti attivi`}
															</span>
														</div>
													)}
												</>
											)}
										</div>
									)}
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFSelectNew
											options={contractTypeOptions}
											value={fields.contractType}
											onChange={(v) => fieldChange("contractType", v)}
											label="Tipologia"
											required
											helperText={
												errors.indexOf("contractType") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("contractType") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFDatePicker
											label="Data stipula"
											value={fields.signingDate}
											onChange={(v) => fieldChange("signingDate", v)}
											required
											format="DD-MM-YYYY"
											size="small"
											helperText={
												errors.indexOf("signingDate") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("signingDate") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFDatePicker
											label="Data inizio"
											value={fields.startDate}
											onChange={(v) => fieldChange("startDate", v)}
											required
											format="DD-MM-YYYY"
											size="small"
											helperText={
												errors.indexOf("startDate") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("startDate") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFDatePicker
											label="Data scadenza"
											value={fields.endDate}
											onChange={(v) => fieldChange("endDate", v)}
											required
											format="DD-MM-YYYY"
											size="small"
											helperText={
												errors.indexOf("endDate") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("endDate") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFSelectNew
											options={currencyOptions}
											value={fields.currency}
											onChange={(v) => fieldChange("currency", v)}
											label="Valuta"
											isClearable={false}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<FormControlLabel
											control={
												<Checkbox
													size="small"
													checked={!!fields.isActive}
													onChange={(e) =>
														fieldChange("isActive", e.target.checked)
													}
												/>
											}
											label="Contratto attivo"
										/>
									</div>
								</div>
							}
						/>
					</div>

					{/* Card importi */}
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni economiche"
							content={
								<div className="row">
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFTextField
											value={fields.transferFee}
											onChange={(e) =>
												fieldChange("transferFee", e.target.value)
											}
											label="Costo cartellino (€)"
											size="small"
											type="number"
											inputProps={{min: 0, step: 100}}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFTextField
											value={fields.fixedMonthlySalary}
											onChange={(e) =>
												fieldChange("fixedMonthlySalary", e.target.value)
											}
											label="Stipendio fisso mensile (€)"
											size="small"
											type="number"
											inputProps={{min: 0, step: 10}}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFTextField
											value={fields.totalMonthlySalary}
											onChange={(e) =>
												fieldChange("totalMonthlySalary", e.target.value)
											}
											label="Totale mensile stimato (€)"
											required
											size="small"
											type="number"
											inputProps={{min: 0, step: 10}}
											helperText={
												errors.indexOf("totalMonthlySalary") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("totalMonthlySalary") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFTextField
											value={fields.annualGrossSalary}
											onChange={(e) =>
												fieldChange("annualGrossSalary", e.target.value)
											}
											label="Retribuzione annua lorda (€)"
											size="small"
											type="number"
											inputProps={{min: 0, step: 100}}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFTextField
											value={fields.signingBonus}
											onChange={(e) =>
												fieldChange("signingBonus", e.target.value)
											}
											label="Bonus alla firma (€)"
											size="small"
											type="number"
											inputProps={{min: 0, step: 100}}
										/>
									</div>
									<div className="col-sm-12 col-md-6 col-lg-4">
										<VLFTextField
											value={fields.releaseClause}
											onChange={(e) =>
												fieldChange("releaseClause", e.target.value)
											}
											label="Clausola rescissoria (€)"
											size="small"
											type="number"
											inputProps={{min: 0, step: 100}}
										/>
									</div>
								</div>
							}
						/>
					</div>

					{/* Card note */}
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Note"
							content={
								<div className="row">
									<div className="col-sm-12">
										<VLFTextField
											value={fields.notes}
											onChange={(e) => fieldChange("notes", e.target.value)}
											label="Note"
											size="small"
											multiline
											minRows={3}
											maxRows={6}
										/>
									</div>
								</div>
							}
						/>
					</div>

					{/* Card bonus */}
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Bonus"
							content={
								<div className="bonuses-container">
									{bonuses.length === 0 && (
										<div className="no-bonuses">Nessun bonus aggiunto</div>
									)}
									{bonuses.map((bonus, index) => (
										<div key={bonus._key} className="bonus-row row">
											<div className="col-sm-12 col-md-6 col-lg-4">
												<VLFTextField
													value={bonus.description}
													onChange={(e) =>
														updateBonus(index, "description", e.target.value)
													}
													label="Descrizione *"
													size="small"
												/>
											</div>
											<div className="col-sm-12 col-md-6 col-lg-4">
												<VLFSelectNew
													options={bonusTypeOptions}
													value={bonus.bonusType}
													onChange={(v) => updateBonus(index, "bonusType", v)}
													label="Tipo bonus"
												/>
											</div>
											<div className="col-sm-12 col-md-6 col-lg-4">
												<VLFTextField
													value={bonus.amount}
													onChange={(e) =>
														updateBonus(index, "amount", e.target.value)
													}
													label="Importo (€) *"
													size="small"
													type="number"
													inputProps={{min: 0, step: 100}}
												/>
											</div>
											<div className="col-sm-12 col-md-6 col-lg-4">
												<VLFTextField
													value={bonus.condition}
													onChange={(e) =>
														updateBonus(index, "condition", e.target.value)
													}
													label="Condizione"
													size="small"
												/>
											</div>
											<div className="col-sm-12 col-md-6 col-lg-4">
												<VLFSelectNew
													options={frequencyOptions}
													value={bonus.frequency}
													onChange={(v) => updateBonus(index, "frequency", v)}
													label="Frequenza"
												/>
											</div>
											<div className="col-sm-12 col-md-6 col-lg-4">
												<VLFTextField
													value={bonus.maxOccurrencesPerSeason}
													onChange={(e) =>
														updateBonus(
															index,
															"maxOccurrencesPerSeason",
															e.target.value,
														)
													}
													label="Max occorrenze/stagione"
													size="small"
													type="number"
													inputProps={{min: 1}}
												/>
											</div>
											<div className="col-sm-12 col-md-12 col-lg-4">
												<VLFTextField
													value={bonus.notes}
													onChange={(e) =>
														updateBonus(index, "notes", e.target.value)
													}
													label="Note bonus"
													size="small"
												/>
											</div>
											<div className="col-auto d-flex align-items-center">
												<IconButton
													color="error"
													size="small"
													onClick={() => removeBonus(index)}
													title="Rimuovi bonus"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</div>
											{index < bonuses.length - 1 && (
												<div className="col-sm-12">
													<hr className="bonus-divider" />
												</div>
											)}
										</div>
									))}
									<div className="mt-2">
										<MainActions
											actions={[
												{
													icon: <AddIcon />,
													label: "Aggiungi bonus",
													variant: "outlined",
													onClick: () =>
														setBonuses((prev) => [...prev, emptyBonus()]),
												},
											]}
										/>
									</div>
								</div>
							}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleContractPage;
